import React from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { Colors, MooncakeTheme } from '../theme'
import breakpoints from '../theme/breakpoints'
import Button from './Button'
import Card from './Card'
import Icon, { ICONS } from './Icon'
import Stack from './Stack'

export type PrimaryCardProps = {
  icon?: keyof typeof ICONS
  iconColor?: Colors
  title: React.ReactNode
  titleStyles?: string
  subtext?: React.ReactNode
  additionalContent?: React.ReactNode
  action?: React.ReactComponentElement<typeof Button>
  subaction?: React.ReactElement
  disabled?: boolean
  testId?: string
  banner?: React.ReactElement
  bannerBackground?: keyof MooncakeTheme['colors']
  before?: React.ReactNode
  after?: React.ReactNode
}

const CardContent = styled.div`
  display: flex;
  flex-direction: column;

  ${breakpoints.tablet} {
    flex-direction: row;
  }
`

const TitleContent = styled.div<{ titleStyles?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${breakpoints.tablet} {
    justify-content: left;
  }

  ${props => props.titleStyles};
`

const TextContent = styled(Stack)`
  b {
    font-weight: ${props => props.theme.weights.medium};
  }

  ${breakpoints.tablet} {
    flex: 1;
    margin-right: 3rem;
  }
`

const Title = styled.span<{ disabled?: boolean }>`
  margin: 0;
  line-height: 1.5rem;
  font-size: 1.125rem;
  color: ${props => (props.disabled ? props.theme.colors.disabledLabel : props.theme.colors.label)};
  font-weight: ${props => props.theme.weights.medium};
  letter-spacing: 0.01em;
`

const Subtext = styled.span<{ disabled?: boolean }>`
  margin: 0;
  line-height: 1.5rem;
  font-size: 1rem;
  color: ${props => (props.disabled ? props.theme.colors.disabledLabel : props.theme.colors.secondaryLabel)};
  font-weight: ${props => props.theme.weights.regular};
  letter-spacing: 0.01em;
`

const SubactionContainer = styled.span`
  display: flex;
  height: 1.5rem;
  align-items: center;
  line-height: 1.5rem;
  color: ${props => props.theme.colors.tooltip};
  margin-left: 0.625rem;
`

const ActionContainer = styled.div`
  margin-top: 1.5rem;

  ${breakpoints.tablet} {
    margin-top: 0;
    flex-basis: 11rem;
  }
`

const BannerContainer = styled.div<{ background: PrimaryCardProps['bannerBackground'] }>`
  margin: -1.5rem;
  margin-top: 1.5rem;
  border-bottom-left-radius: 1.5rem;
  border-bottom-right-radius: 1.5rem;
  padding: 1.5rem;
  padding-top: ${props => (props.background ? '1.5rem' : '0')};
  background: ${props => (props.background ? props.theme.colors[props.background] : 'transparent')};
`

const PrimaryCard = ({
  icon,
  iconColor = 'primary',
  title,
  titleStyles,
  subtext,
  additionalContent,
  action,
  subaction,
  testId,
  disabled,
  banner,
  bannerBackground,
  before,
  after,
}: PrimaryCardProps) => {
  const theme = useTheme()

  return (
    <Card data-testid={testId} disabled={disabled}>
      {before}
      <CardContent>
        <TextContent space="1.5rem">
          <Stack space="0.5rem">
            <Stack space="1rem">
              {icon && <Icon color={theme.colors[iconColor]} name={icon} height="2.5rem" width="2.5rem" />}
              <TitleContent titleStyles={titleStyles}>
                <Title disabled={disabled}>{title}</Title>
                {subaction && <SubactionContainer>{subaction}</SubactionContainer>}
              </TitleContent>
            </Stack>
            {subtext && <Subtext disabled={disabled}>{subtext}</Subtext>}
          </Stack>
          {additionalContent && <div>{additionalContent}</div>}
        </TextContent>
        {action && <ActionContainer>{action}</ActionContainer>}
      </CardContent>
      {banner && <BannerContainer background={bannerBackground}>{banner}</BannerContainer>}
      {after}
    </Card>
  )
}

export default PrimaryCard
